import type { CalendarEventDto } from '@chat-emp/shared';

import type { ChannelId, ParticipantId } from '../fixtures/domain.js';
import { fromDateTimeInput, toDateInput, toTimeInput } from '../util/format.js';
import { minutesAfter, type TimeSpan } from './event-rules.js';

/**
 * The lengths a Chat Day actually runs, one press each.
 *
 * Hours, not minutes: this is a window an employer blocks out, not the interview
 * form's half-hour slot. Two is the short one — a lunchtime window for a dozen
 * people — and the long one is named "Half day" rather than "4 hr" because that
 * is what somebody blocking out a morning calls it.
 *
 * Here rather than in the dialog that draws them because the first of them is
 * also the length a fresh draft opens at, and a default that disagrees with the
 * pill it should light up is a form that opens with nothing pressed.
 */
export const SPANS: { minutes: number; label: string }[] = [
  { minutes: 120, label: '2 hr' },
  { minutes: 180, label: '3 hr' },
  { minutes: 240, label: 'Half day' },
];

/** How long a Chat Day is before anybody says otherwise. See `SPANS`. */
export const CHAT_DAY_MINUTES = SPANS[0]!.minutes;

/**
 * Somebody the Chat Day is for.
 *
 * Everyone reachable here already has an open chat (the picker offers nothing
 * else — see AI-2713), so the channel is not optional and there is no "saving
 * will open one for them" case to carry. That is what lets this be a flat record
 * rather than the mock's `{ invitee, from }` pair: the application a guest was
 * found through only mattered when a chat had to be opened *about* something.
 */
export interface Guest {
  /** The Stream member form (`js_…`), so it lines up with channel members. */
  id: ParticipantId;
  name: string;
  avatarUrl?: string;
  locked?: boolean;
  /** The open chat the invite gets posted into. */
  channelId: ChannelId;
}

/**
 * A Chat Day while it is being written.
 *
 * The times are the strings the fields edit in rather than Dates, because
 * mid-edit there may not *be* a time: a cleared date field is "", which no Date
 * can hold, and a draft that cannot represent what is on screen is a draft the
 * form has to work around.
 *
 * No `kind` — everything on this form is a Chat Day. No `where` — see `missing`.
 * No reminders: calendar-service drives those from `user_settings.reminder_config`
 * per user, so a per-event reminder has nowhere to be stored.
 */
export interface EventDraft {
  title: string;
  /** `YYYY-MM-DD`. */
  date: string;
  /** `HH:mm`. */
  from: string;
  /** `HH:mm`. */
  to: string;
  invitees: Guest[];
  /** What to expect on the day. This rides the invite card (AI-2715). */
  description: string;
}

/** A new Chat Day, from the day that was open when the button was pressed. */
export function newDraft(start: Date): EventDraft {
  return {
    title: '',
    date: toDateInput(start),
    from: toTimeInput(start),
    // It opens at a real length so the block is on the grid the moment it is
    // booked, and at the first pill's so the pills open with one pressed.
    to: toTimeInput(minutesAfter(start, CHAT_DAY_MINUTES)),
    invitees: [],
    description: '',
  };
}

/** A saved event as the form shows it. Nobody new is on it yet. */
export function draftOf(event: CalendarEventDto): EventDraft {
  const start = new Date(event.startsAt);
  return {
    title: event.title,
    date: toDateInput(start),
    from: toTimeInput(start),
    to: toTimeInput(new Date(event.endsAt)),
    invitees: [],
    description: event.description ?? '',
  };
}

const DATE_INPUT = /^\d{4}-\d{2}-\d{2}$/;
const TIME_INPUT = /^\d{1,2}:\d{2}$/;

/**
 * When the draft starts, or null while the fields do not yet describe a moment.
 *
 * The *shape* of the strings is checked, not the Date they parse to, and that is
 * the whole reason this exists: `Number('')` is 0, so a cleared date field parses
 * to a perfectly valid 1st of January 1900 rather than to `NaN`. Trusting the
 * parse — as the form this was ported from does — means `missing` can never
 * report "when", and Save sails past an unanswered date onto a Chat Day in 1900.
 */
export function draftStart(draft: EventDraft): Date | null {
  if (!DATE_INPUT.test(draft.date) || !TIME_INPUT.test(draft.from)) return null;
  const start = fromDateTimeInput(draft.date, draft.from);
  return Number.isNaN(start.getTime()) ? null : start;
}

/**
 * When the draft runs, or null while it has no start at all.
 *
 * An end at or before the start is not a range, and rather than refusing to
 * place it this gives the start its default length back: nobody sets an end
 * time in order to have no event, and the block has to be somewhere while they
 * are still typing.
 */
export function draftSpan(draft: EventDraft): TimeSpan | null {
  const start = draftStart(draft);
  if (!start) return null;

  const parsed = fromDateTimeInput(draft.date, draft.to);
  const end =
    !Number.isNaN(parsed.getTime()) && parsed > start
      ? parsed
      : minutesAfter(start, CHAT_DAY_MINUTES);

  return { startsAt: start.toISOString(), endsAt: end.toISOString() };
}

/** The fields a Chat Day cannot be saved without. Description is the only optional one. */
export type EventField = 'title' | 'when' | 'invitees';

/**
 * Which of them this draft has no answer for yet.
 *
 * Fields rather than a sentence, because the answer is shown on the fields
 * themselves. A line of prose in the footer ("Still needs a title and someone to
 * invite") reads as a complaint, wraps the footer as it grows, and still leaves
 * the employer to map the phrases back onto the controls. Marking the controls
 * skips the mapping.
 *
 * Nothing is marked until Save is pressed (see `gapsShown`), so an empty form is
 * an empty form rather than three things already wrong with it.
 *
 * **No `where`.** A Chat Day is held in the chats by definition — that is the
 * whole idea — so a venue is not a gap here, it is a question that could only
 * ever be answered wrongly.
 */
export function missing(draft: EventDraft): EventField[] {
  const gaps: EventField[] = [];
  if (!draft.title.trim()) gaps.push('title');
  if (!draftSpan(draft)) gaps.push('when');
  // The one field that cannot be filled in later from the chat itself: a Chat
  // Day with nobody on it tells nobody anything, and the invite it sends is the
  // whole point of booking one.
  if (draft.invitees.length === 0) gaps.push('invitees');
  return gaps;
}
